import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Lock, Eye, EyeOff } from "lucide-react";
import { usePinMutations } from "../hooks/usePin";
import { safeAuthErrorMessage } from "../utils/safeApiErrorMessage";

const SectionLabel = ({ children }) => (
  <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
    {children}
  </h2>
);

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const PIN_LENGTH = 6;

function PinInput({ value, onChange, label, autoFocus }) {
  const [visible, setVisible] = useState(false);

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex items-center gap-3 bg-[#0A4730]/5 rounded-2xl px-4 py-3 ring-1 ring-[#0A4730]/10 focus-within:ring-[#0A4730]/30 transition">
        <Lock size={16} className="text-[#0A4730]/40 shrink-0" />
        <input
          type={visible ? "text" : "password"}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={PIN_LENGTH}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH);
            onChange(digits);
          }}
          placeholder="••••••"
          className="bg-transparent text-lg font-bold tracking-[0.4em] outline-none w-full text-[#0A2540] placeholder-[#0A2540]/15"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="text-[#0A4730]/40 shrink-0"
          tabIndex={-1}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export function SetupPin() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/home";

  const { setupPinAsync, isSettingUpPin, setupPinError } = usePinMutations();

  const [currentPassword, setCurrentPassword] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [formError, setFormError] = useState(null);

  async function handleSubmit() {
    setFormError(null);

    if (!currentPassword) {
      setFormError("Enter your account password to confirm it's you.");
      return;
    }
    if (pin.length !== PIN_LENGTH) {
      setFormError(`PIN must be ${PIN_LENGTH} digits.`);
      return;
    }
    if (pin !== confirmPin) {
      setFormError("PINs don't match.");
      return;
    }

    try {
      await setupPinAsync({ current_password: currentPassword, pin });
      navigate(returnTo, { replace: true, state: { pinSetupComplete: true } });
    } catch (err) {
      setFormError(
        safeAuthErrorMessage(err, "Couldn't set up your PIN. Please try again.")
      );
    }
  }

  const mutationErrorMessage = setupPinError
    ? safeAuthErrorMessage(setupPinError, "Couldn't set up your PIN. Please try again.")
    : null;

  const submitting = isSettingUpPin;

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left">

      {/* HEADER — mobile only */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft className="text-white" size={20} />
          </button>
          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">Security</p>
            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Set Up Your PIN
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:flex items-center gap-3 max-w-xl mx-auto px-5 pt-10 pb-2">
        <button
          onClick={() => navigate(-2)}
          className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft size={18} />
        </button>
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          Set Up Your PIN
        </h1>
      </div>

      {/* CONTENT */}
      <div className="max-w-xl mx-auto px-5 py-8 space-y-8">

        <p className="text-sm text-[#0A2540]/50 leading-relaxed">
          Your transaction PIN adds a second check before money moves - you'll
          be asked for it whenever you confirm a transfer.
        </p>

        <div className="space-y-2.5">
          <SectionLabel>Confirm It's You</SectionLabel>
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-4">
            <FieldLabel>Account Password</FieldLabel>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoFocus
              className="bg-transparent text-base font-semibold outline-none w-full text-[#0A2540] placeholder-[#0A2540]/20 border-b border-slate-100 pb-2"
              placeholder="Enter your password"
            />
          </div>
        </div>

        <div className="space-y-2.5">
          <SectionLabel>Choose Your PIN</SectionLabel>
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-4 space-y-4">
            <PinInput value={pin} onChange={setPin} label={`${PIN_LENGTH}-Digit PIN`} />
            <PinInput value={confirmPin} onChange={setConfirmPin} label="Confirm PIN" />
          </div>
          <p className="text-[10px] leading-relaxed text-[#0A2540]/40 px-1">
            Avoid obvious sequences like 123456 or repeated digits - this PIN
            protects every transfer you make.
          </p>
        </div>

        {(formError || mutationErrorMessage) && (
          <div role="alert" className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-sm">
            {formError || mutationErrorMessage}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98] disabled:opacity-60"
        >
          {submitting ? "Setting up your PIN…" : "Set PIN"}
        </button>

      </div>
    </div>
  );
}
